import type { SiteStatusSummary } from '../../domain/siteNote';
import type { SitePenCounts } from '../../domain/sitePenCounts';
import { csvDocument } from '../../lib/csv';
import { siteLabel } from '../../lib/site-label';
import {
  siteGenerationLabels,
  type CustomerSiteCountryGroup,
  type CustomerSiteInsight,
} from '../customers/customer-insights';
import { describeCompleteness } from './site-completeness';
import type { SiteStatusTotals } from './site-status-totals';

const HEADER = [
  'Level',
  'Country',
  'Region',
  'Site',
  'Generation',
  'Ordered',
  'Systems',
  'Active',
  'Pens with fish',
  'Producing data',
  'Farm data from',
  'Missing / extra',
  'Open requests',
  'Last contact',
  'Latest note date',
  'Latest note',
];

/** YYYY-MM-DD, which spreadsheets sort and read as a date. */
function day(timestamp: string | undefined): string {
  return timestamp ? timestamp.slice(0, 10) : '';
}

/**
 * The customer's site status table as CSV (tidaltech/koi#8469): every
 * site the table shows, whether or not its region is open, under its
 * country and region totals, after an all-sites total. Values are the
 * table's; the sites the table hides (nothing ordered or on site) stay out.
 */
export function siteStatusCsv(
  groups: readonly CustomerSiteCountryGroup[],
  totals: (sites: readonly CustomerSiteInsight[]) => SiteStatusTotals,
  pensBySite: { get(siteId: string): SitePenCounts | undefined },
  openRequestsBySite: ReadonlyMap<string, number>,
  statusBySite: ReadonlyMap<string, SiteStatusSummary>,
): string {
  const rows: (string | number | undefined)[][] = [HEADER];
  const totalRow = (
    level: 'All sites' | 'Country' | 'Region',
    country: string,
    region: string,
    sum: SiteStatusTotals,
  ) => {
    rows.push([
      level,
      country,
      region,
      `${sum.sites} ${sum.sites === 1 ? 'site' : 'sites'}`,
      '',
      sum.ordered,
      sum.systems,
      sum.active,
      sum.pensWithFish,
      sum.pensWithData,
      '',
      sum.sitesWithGaps > 0
        ? `${sum.sitesWithGaps} ${sum.sitesWithGaps === 1 ? 'site' : 'sites'} with gaps`
        : '',
      sum.openRequests,
      day(sum.lastContact),
      '',
      '',
    ]);
  };
  const allSites = groups.flatMap((country) => country.regions.flatMap((region) => region.sites));
  totalRow('All sites', '', '', totals(allSites));
  for (const country of groups) {
    totalRow(
      'Country',
      country.label,
      '',
      totals(country.regions.flatMap((region) => region.sites)),
    );
    for (const region of country.regions) {
      totalRow('Region', country.label, region.label, totals(region.sites));
      for (const insight of region.sites) {
        const { site, completeness, generation } = insight;
        const pens = pensBySite.get(site.id);
        const status = statusBySite.get(site.id);
        const gaps = describeCompleteness(completeness);
        rows.push([
          'Site',
          country.label,
          region.label,
          siteLabel(site),
          generation === 'unclassified' ? '' : siteGenerationLabels[generation],
          site.orderedSystems,
          completeness.assembled,
          completeness.active,
          pens?.pensWithFish,
          pens?.pensWithData,
          pens?.date,
          gaps.length > 0 ? gaps.join('; ') : completeness.expected > 0 ? 'Complete' : '',
          openRequestsBySite.get(site.id) ?? 0,
          day(status?.lastContact?.createTime),
          day(status?.latest?.createTime),
          status?.latest?.text,
        ]);
      }
    }
  }
  return csvDocument(rows);
}
